import type { ConversationSummary } from "@beacon/core/chat"
import { useQueryClient } from "@tanstack/react-query"
import { RefreshCwIcon } from "lucide-react"
import { useEffect, useState, useSyncExternalStore } from "react"
import { useLocation } from "react-router"
import { useOnline } from "@/installed-app/connection.tsx"
import { chatKeys, fetchLiveTicket, forgetConversation, refreshConversation } from "./client.ts"
import { refreshInbox } from "@/beacon-inbox/client.ts"
import { attentionKey } from "./attention.ts"
import { effectOf } from "./live-hints.ts"
import { createLiveSocket, type ChatHint, type LiveSocket, type LiveStatus } from "./live-socket.ts"
import { activityWatch, presenceKeys, touchesMember } from "./presence.ts"

/*
 * Chat, live, for the signed-in tab: one socket (`live-socket.ts`) for the whole shell, held by
 * `ChatLiveHost` while the shell is open, so the sidebar's conversations, an open conversation and
 * its people follow each change as it happens. Each hint is read here first: a conversation the
 * Member can no longer read is forgotten, and one whose people changed, or a direct message that
 * moved, is read again; a message arriving or going, or another of the Member's devices reading,
 * reads what is unread to them again (`effectOf`). An open timeline listens too, and catches up from its cursor
 * (`useTimeline`), as it does each time the socket (re)opens. A colleague's presence changing reads
 * their presence again wherever it shows. The host also tells the socket whether the Member is
 * idle (`activityWatch`), which is what keeps them online to their colleagues. A Document's page
 * subscribes to its Document over the same socket, and hears its Draft move (`useLiveDraft`).
 */

export type ChatLive = {
  status: LiveStatus
  /** Asks to be told of a conversation, joined or not, while on screen; answers how to stop. */
  watch(conversationId: string): () => void
  /** Asks to be told each time a Document's Draft moves, while it is open; answers how to stop. */
  subscribe(documentId: string): () => void
  /** Tells the conversation's others the Member is typing in it, or stopped; nothing when down. */
  typing(conversationId: string, typing: boolean): void
  /** Hears each hint after the shell has; answers how to stop. */
  onHint(listener: (hint: ChatHint) => void): () => void
  /** Hears each (re)opening of the socket, when whatever was missed is to be caught up. */
  onOpen(listener: () => void): () => void
}

const hearing = new Set<(hint: ChatHint) => void>()
const opening = new Set<() => void>()
const changing = new Set<() => void>()
let socket: LiveSocket | undefined
// Every conversation asked to be watched, however many ask, and how to stop the socket watching
// it: one asked for before the host starts its socket (a page mounts before the shell's last
// child) is handed to the socket as it starts. Every Document subscribed to, likewise.
type Asked = Map<string, { count: number; stop?: () => void }>
const watching: Asked = new Map()
const subscribing: Asked = new Map()

function listen<T>(listeners: Set<T>, listener: T) {
  listeners.add(listener)
  return () => void listeners.delete(listener)
}

/** Asks `asked` for `id`, handing it to the socket through `start` when there is one now. */
function ask(asked: Asked, id: string, start: (socket: LiveSocket) => () => void) {
  const entry = asked.get(id) ?? { count: 0 }
  asked.set(id, entry)
  entry.count++
  if (entry.count === 1 && socket) entry.stop = start(socket)
  let done = false
  return () => {
    if (done) return
    done = true
    entry.count--
    if (entry.count > 0) return
    entry.stop?.()
    asked.delete(id)
  }
}

const watch = (conversationId: string) =>
  ask(watching, conversationId, (held) => held.watch(conversationId))
const subscribe = (documentId: string) =>
  ask(subscribing, documentId, (held) => held.subscribe(documentId))

/** With no host (outside the shell, or before it starts): no socket, so timelines use a timer. */
let snapshot: ChatLive = {
  status: "unavailable",
  watch,
  subscribe,
  typing: (conversationId, typing) => socket?.typing(conversationId, typing),
  onHint: (listener) => listen(hearing, listener),
  onOpen: (listener) => listen(opening, listener),
}

function setStatus(status: LiveStatus) {
  if (snapshot.status === status) return
  snapshot = { ...snapshot, status }
  for (const changed of changing) changed()
}

/** Chat's live socket, as the shell holds it. */
export function useChatLive() {
  return useSyncExternalStore(
    (changed) => listen(changing, changed),
    () => snapshot,
  )
}

/** How often the host checks whether the Member has gone idle. */
const idleCheckEvery = 30_000

/** What says the Member is using the page. */
const usedOn = ["pointerdown", "keydown", "wheel", "touchstart", "focus"] as const

/**
 * Holds the tab's live socket while the shell is open, for the signed-in Member `me`, and acts on
 * each hint for the shell. Renders nothing; one per shell.
 */
export function ChatLiveHost({ me }: { me: string }) {
  const queryClient = useQueryClient()

  useEffect(() => {
    const held = createLiveSocket({
      ticket: fetchLiveTicket,
      open: (url) => new WebSocket(url),
      onStatus: setStatus,
      onOpen() {
        // Whatever happened while it was down, the Member's own list and read state included:
        // another device may have read meanwhile.
        void queryClient.invalidateQueries({ queryKey: chatKeys.conversations })
        void queryClient.invalidateQueries({ queryKey: chatKeys.unreads })
        void queryClient.invalidateQueries({ queryKey: chatKeys.threads })
        void queryClient.invalidateQueries({ queryKey: chatKeys.mentions })
        void queryClient.invalidateQueries({ queryKey: attentionKey })
        void refreshInbox(queryClient)
        for (const listener of opening) listener()
      },
      onHint(hint) {
        if (hint.kind === "presence") {
          const { memberId } = hint
          void queryClient.invalidateQueries({
            predicate: ({ queryKey }) => touchesMember(queryKey, memberId),
          })
          if (memberId === me)
            void queryClient.invalidateQueries({ queryKey: presenceKeys.availability })
        }
        if (hint.kind === "presence" || hint.kind === "typing") {
          for (const listener of hearing) listener(hint)
          return
        }
        const mine = queryClient.getQueryData<ConversationSummary[]>(chatKeys.conversations)
        const effect = effectOf(hint, mine)
        const at = "conversationId" in hint ? hint.conversationId : undefined
        if (effect.forget && at) forgetConversation(queryClient, at)
        if (effect.refreshView && at) void refreshConversation(queryClient, at)
        if (effect.refreshList)
          void queryClient.invalidateQueries({ queryKey: chatKeys.conversations })
        if (effect.refreshUnreads)
          void queryClient.invalidateQueries({ queryKey: chatKeys.unreads })
        if (effect.refreshThreads)
          void queryClient.invalidateQueries({ queryKey: chatKeys.threads })
        if (effect.refreshMentions)
          void queryClient.invalidateQueries({ queryKey: chatKeys.mentions })
        if (effect.refreshAttention) void queryClient.invalidateQueries({ queryKey: attentionKey })
        if (effect.refreshInbox) void refreshInbox(queryClient)
        for (const listener of hearing) listener(hint)
      },
    })
    socket = held
    for (const [conversationId, entry] of watching) entry.stop = held.watch(conversationId)
    for (const [documentId, entry] of subscribing) entry.stop = held.subscribe(documentId)
    // Online to colleagues while the Member uses the page; idle (away) once they haven't for a while.
    const activity = activityWatch({ onIdle: (idle) => held.presence(idle) })
    held.presence(false)
    const used = () => activity.used()
    for (const event of usedOn) window.addEventListener(event, used, { passive: true })
    const checking = setInterval(() => activity.check(), idleCheckEvery)
    held.start()
    // Back on the network, or back to the tab: no waiting out the backoff.
    const retry = () => held.retry()
    const shown = () => {
      if (document.visibilityState !== "visible") return
      activity.used()
      held.retry()
    }
    window.addEventListener("online", retry)
    document.addEventListener("visibilitychange", shown)
    return () => {
      window.removeEventListener("online", retry)
      document.removeEventListener("visibilitychange", shown)
      for (const event of usedOn) window.removeEventListener(event, used)
      clearInterval(checking)
      held.stop()
      socket = undefined
      for (const entry of [...watching.values(), ...subscribing.values()]) entry.stop = undefined
      setStatus("unavailable")
    }
  }, [queryClient, me])

  return null
}

/** How long the socket may be down before Chat's pages say so: longer than a renewal takes. */
const quietFor = 5_000

/**
 * The slim banner atop a Chat page while its live socket is down and reconnecting, online (offline,
 * the shell's own banner says so): messages still arrive, by catching up on a timer, only later.
 */
export function ChatLiveBanner() {
  const { status } = useChatLive()
  const online = useOnline()
  const { pathname } = useLocation()
  const down = online && status === "reconnecting" && /^\/chat(\/|$)/.test(pathname)
  const [shown, setShown] = useState(false)
  useEffect(() => {
    if (!down) return setShown(false)
    const timer = setTimeout(() => setShown(true), quietFor)
    return () => clearTimeout(timer)
  }, [down])
  if (!down || !shown) return null
  return (
    <div
      role="status"
      className="flex shrink-0 items-center justify-center gap-2 border-b border-border-subtle bg-muted px-4 py-1.5 text-xs text-muted-foreground"
    >
      <RefreshCwIcon aria-hidden className="size-3.5 shrink-0" />
      {/* A phone's line holds the short form, so the banner never pushes the header down two. */}
      <span className="compact:hidden">
        Reconnecting to Chat. New messages may take a few seconds longer to appear.
      </span>
      <span className="hidden compact:inline">Reconnecting to Chat…</span>
    </div>
  )
}
